export const FTP_KEY = 'trainer/playerFtp'

// Rolling cadence samples that drive automatic pause/resume. Because the
// player ticks once per second, "2 s cadence average" is the last two cadence
// readings (integer rpm), whose average is 0 exactly when both samples are 0.
export const CADENCE_STOP_SAMPLES = 2

export function pushCadenceSample(ring, value, maxSamples = CADENCE_STOP_SAMPLES) {
    if (value == null) return ring
    return [...ring, value].slice(-maxSamples)
}

export function cadenceStopped(ring) {
    return ring.length >= CADENCE_STOP_SAMPLES && ring.every(v => v === 0)
}

export function cadenceResuming(ring) {
    if (ring.length < 2) return false
    const prev = ring[ring.length - 2]
    const last = ring[ring.length - 1]
    return last > 0 && last > prev
}

export const POWER_ZONE_LIMITS = [
    ['recovery', 55],
    ['endurance', 75],
    ['tempo', 87],
    ['sweet_spot', 94],
    ['threshold', 105],
    ['vo2max', 120],
]

const ZONE_LABELS = {
    recovery: 'Восстановление',
    endurance: 'Аэробная',
    tempo: 'Темп',
    sweet_spot: 'Sweet Spot',
    threshold: 'Порог',
    vo2max: 'VO2max',
    anaerobic: 'Анаэробная',
}

export function clampPct(v) {
    return Math.min(130, Math.max(0, Number(v) || 0))
}

export function expandDesignerRows(rows) {
    const points = []
    let t = 0
    ;(rows || []).forEach(row => {
        const secs = Math.max(1, Math.round(Number(row.time_s) || 0))
        const pct = clampPct(Number(row.target_pct))
        const repeats = Math.max(1, Math.round(Number(row.repeats) || 1))
        for (let r = 0; r < repeats; r++) {
            points.push({ t, v: pct })
            t += secs
            points.push({ t, v: pct })
        }
    })
    return points
}

export function computeTimeSeries(points) {
    const y = []
    let t = 0
    let lastX = 0
    let lastY = 0
    ;(points || []).forEach(interval => {
        const ti = Math.round(interval.t)
        const v = interval.v
        if (t === 0 && ti === 0) {
            y.push(v)
            t += 1
        } else {
            const denom = (ti - lastX) || 1
            const start = lastX + 1
            for (let x = start; x <= ti; x++) {
                const ratio = (x - lastX) / denom
                y.push(Math.round(lastY + ratio * (v - lastY)))
            }
            t += (ti - start + 1)
        }
        lastX = ti
        lastY = v
    })
    return y
}

export function targetWatts(pct, ftp) {
    return Math.round((Number(pct) || 0) * (Number(ftp) || 0) / 100)
}

export function percentOfFtp(watts, ftp) {
    if (!ftp || watts == null) return null
    return watts / ftp * 100
}

export function powerZoneLabel(pctOfFtp) {
    if (pctOfFtp == null || Number.isNaN(pctOfFtp)) return null
    for (const [key, limit] of POWER_ZONE_LIMITS) {
        if (pctOfFtp <= limit) return ZONE_LABELS[key]
    }
    return ZONE_LABELS.anaerobic
}

export function workoutTargetSeries(points, ftp) {
    return computeTimeSeries(points).map((pct, time) => ({
        time,
        pct,
        targetWatts: targetWatts(pct, ftp),
    }))
}

// Display-resolution cap for the player chart: the per-second series of a
// multi-hour workout reaches tens of thousands of points, which makes the G2
// chart mount/update/GC cost heavy enough to stall the main thread. The
// stepped power profile survives equal-spaced sampling, so keep the last point
// (it defines the x-domain end) and drop everything above maxPoints.
export function decimateSeries(series, maxPoints = 2400) {
    if (!series || series.length <= maxPoints) return series
    const step = (series.length - 1) / (maxPoints - 1)
    const out = []
    const taken = new Set()
    for (let i = 0; i < maxPoints; i++) {
        const idx = Math.round(i * step)
        if (taken.has(idx)) continue
        taken.add(idx)
        out.push(series[idx])
    }
    const last = series.length - 1
    if (!taken.has(last)) out.push(series[last])
    return out
}

export function intervalRemaining(series, elapsed) {
    const current = Math.floor(elapsed)
    if (!series || !series.length || current >= series.length - 1) return 0
    const curPct = series[current].pct
    for (let i = current + 1; i < series.length; i++) {
        if (series[i].pct !== curPct) return i - current
    }
    return series.length - 1 - current
}

export function averagePower3s(samples) {
    const pwrs = (samples || []).map(s => s.power).filter(p => p != null)
    const last = pwrs.slice(-3)
    if (!last.length) return null
    return Math.round(last.reduce((a, b) => a + b, 0) / last.length)
}

export function classifySensorState({ mode, lastGoodAt, now, staleAfterMs = 3500 }) {
    if (mode !== 'configured') return 'disconnected'
    if (lastGoodAt != null && (now - lastGoodAt) <= staleAfterMs) return 'connected'
    return 'stale'
}

export function currentMetric(sensorsData, name) {
    const metrics = sensorsData && sensorsData.metrics
    if (Array.isArray(metrics)) {
        const m = metrics.find(x => x.name === name)
        if (m && m.items && m.items.length) return m.items[0].value
    }
    // FE-C fitness equipment routes its live values (pwr/cad/spd/hr) into the
    // `equipment` bucket, not `metrics`; read them back so the training page
    // can display trainer power alongside metrics-form sensors like HR.
    const equipment = sensorsData && sensorsData.equipment
    if (Array.isArray(equipment)) {
        for (const eq of equipment) {
            if (eq && eq.data && eq.data[name] !== undefined) return eq.data[name]
        }
    }
    return undefined
}

export function formatWatts(value) {
    return value == null ? '—' : String(Math.round(value))
}

export function formatHeartRate(value) {
    return value == null ? '—' : String(Math.round(value))
}

export function formatCadence(value) {
    return value == null ? '—' : String(Math.round(value))
}

export function formatPercent(value) {
    return value == null ? '—' : `${Math.round(value)}%`
}

export function hrZoneLabel(hr, maxHr) {
    if (hr == null || !maxHr) return null
    const pct = hr / maxHr * 100
    if (pct < 60) return 'Зона 1'
    if (pct < 70) return 'Зона 2'
    if (pct < 80) return 'Зона 3'
    if (pct < 90) return 'Зона 4'
    return 'Зона 5'
}
